import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { History, Loader2, RotateCcw } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';

const SRC = { fidem: 'Fidem', escrito: 'Botón Escrito', anterior: 'Versión previa', fidem_cambios: 'Fidem · cambios del cliente' };
const label = (v) => `${new Date(v.created_at).toLocaleString('es-CL')} · ${SRC[v.source] || v.source}`;

// Paragraph-level LCS diff: rows of [left, right, state] where state is same | del | add
const diff = (a, b) => {
  const A = a.map((x) => x[1]); const B = b.map((x) => x[1]);
  const L = Array.from({ length: A.length + 1 }, () => new Array(B.length + 1).fill(0));
  for (let i = A.length - 1; i >= 0; i -= 1) for (let j = B.length - 1; j >= 0; j -= 1) L[i][j] = A[i] === B[j] ? L[i + 1][j + 1] + 1 : Math.max(L[i + 1][j], L[i][j + 1]);
  const rows = []; let i = 0; let j = 0;
  while (i < A.length || j < B.length) {
    if (i < A.length && j < B.length && A[i] === B[j]) { rows.push([a[i], b[j], 'same']); i += 1; j += 1; }
    else if (j < B.length && (i >= A.length || L[i][j + 1] >= L[i + 1][j])) { rows.push([null, b[j], 'add']); j += 1; }
    else { rows.push([a[i], null, 'del']); i += 1; }
  }
  return rows;
};

const Cell = ({ blk, tone }) => !blk ? <div /> : (
  <p className={`rounded px-2 py-1 text-xs leading-relaxed ${blk[0] === 'h' || blk[0] === 'suma' ? 'font-bold' : ''} ${tone}`}>{blk[1]}</p>
);

export const BriefVersions = ({ matter, deadline, onRestored }) => {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [a, setA] = useState('');
  const [b, setB] = useState('');
  const [busy, setBusy] = useState(null);
  const open = async () => {
    setBusy('load');
    try {
      const r = (await api.get(`/crm/matters/${matter.id}/brief-versions`, { params: deadline ? { deadline_id: deadline.id } : {} })).data;
      setData(r); setB(r.versions[0]?.id || ''); setA(r.versions[1]?.id || r.versions[0]?.id || '');
    } catch { toast.error('No se pudieron cargar las versiones'); } finally { setBusy(null); }
  };
  const va = data?.versions.find((v) => v.id === a);
  const vb = data?.versions.find((v) => v.id === b);
  const rows = useMemo(() => (va && vb ? diff(va.blocks, vb.blocks) : []), [va, vb]);
  const changed = rows.filter((r) => r[2] !== 'same').length;
  const restore = async (v) => {
    setBusy(v.id);
    try {
      const r = await api.post(`/crm/matters/${matter.id}/brief-versions/${v.id}/restore`);
      setData({ ...data, current_id: v.id }); onRestored(r.data.brief); toast.success('Versión restaurada como escrito actual');
    } catch { toast.error('No se pudo restaurar'); } finally { setBusy(null); }
  };
  const pick = (val, set, id) => (
    <select data-testid={id} value={val} onChange={(e) => set(e.target.value)} className="w-full rounded-lg border border-slate-700 bg-slate-900 px-2 py-1 text-xs text-slate-200">
      {data.versions.map((v) => <option key={v.id} value={v.id}>{label(v)}{v.id === data.current_id ? ' (actual)' : ''}</option>)}
    </select>
  );
  return (
    <>
      <button data-testid="brief-versions-btn" onClick={open} className="flex items-center gap-1 rounded-full border border-slate-600 px-3 py-1.5 text-xs text-slate-200 hover:text-white">
        {busy === 'load' ? <Loader2 className="h-3 w-3 animate-spin" /> : <History className="h-3 w-3" />}Versiones
      </button>
      <Dialog open={!!data} onOpenChange={(o) => !o && setData(null)}>
        <DialogContent data-testid="brief-versions-dialog" className="max-h-[92vh] max-w-6xl overflow-y-auto border-slate-700 bg-slate-950 text-white">
          <DialogHeader>
            <DialogTitle className="text-white">Versiones del escrito · {data?.versions.length || 0}</DialogTitle>
            <DialogDescription className="text-slate-400">Cada escrito que redactas (desde la causa o con Fidem) queda guardado. Compara dos versiones o vuelve a una anterior.</DialogDescription>
          </DialogHeader>
          {data && !data.versions.length && <p data-testid="brief-versions-empty" className="text-sm text-slate-400">Aún no hay versiones guardadas.</p>}
          {data?.versions.length > 0 && (
            <>
              <div className="grid gap-3 md:grid-cols-2">
                <div>{pick(a, setA, 'brief-version-a')}{va && <button data-testid="brief-version-restore-a" disabled={!!busy || va.id === data.current_id} onClick={() => restore(va)} className="mt-1 flex items-center gap-1 text-[11px] text-amber-300 disabled:opacity-40"><RotateCcw className="h-3 w-3" />{va.id === data.current_id ? 'Es la actual' : 'Restaurar esta versión'}</button>}</div>
                <div>{pick(b, setB, 'brief-version-b')}{vb && <button data-testid="brief-version-restore-b" disabled={!!busy || vb.id === data.current_id} onClick={() => restore(vb)} className="mt-1 flex items-center gap-1 text-[11px] text-amber-300 disabled:opacity-40"><RotateCcw className="h-3 w-3" />{vb.id === data.current_id ? 'Es la actual' : 'Restaurar esta versión'}</button>}</div>
              </div>
              <p data-testid="brief-versions-changes" className="text-xs text-slate-400">{a === b ? 'Elige dos versiones distintas para compararlas.' : `${changed} párrafo(s) distintos · rojo: solo en la izquierda · verde: solo en la derecha`}</p>
              {a !== b && <div data-testid="brief-versions-diff" className="grid grid-cols-2 gap-x-3 gap-y-1 rounded-xl border border-slate-800 bg-white/95 p-3 font-serif text-slate-900">
                {rows.map(([l, r, st], i) => (
                  <React.Fragment key={i}>
                    <Cell blk={l} tone={st === 'del' ? 'bg-red-100 text-red-900' : ''} />
                    <Cell blk={r} tone={st === 'add' ? 'bg-emerald-100 text-emerald-900' : ''} />
                  </React.Fragment>
                ))}
              </div>}
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
